import { useTranslation } from 'react-i18next';

import { Badge, Select } from '@saas-kit/ui';

import type { Member, TenantRole } from './api';

/**
 * Additional (custom) roles for one member. The system base role keeps its own
 * dropdown column; this cell assigns/revokes any custom tenant role created in
 * the RBAC editor so newly-created roles are usable without code changes.
 */
export function MemberRolesCell({
  member,
  roles,
  canManage,
  busy,
  onAssign,
  onRevoke,
}: {
  member: Member;
  roles: TenantRole[];
  canManage: boolean;
  busy: boolean;
  onAssign: (roleId: string) => void;
  onRevoke: (roleId: string) => void;
}): JSX.Element {
  const { t } = useTranslation('members');
  const assignedIds = new Set(member.roles.map((role) => role.id));
  const customRoles = member.roles.filter((role) => !role.is_system);
  const assignable = roles.filter(
    (role) => !role.is_system && !assignedIds.has(role.id),
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {customRoles.length === 0 ? (
        <span className="text-xs text-muted-foreground">{t('roles_cell.none')}</span>
      ) : (
        customRoles.map((role) => (
          <span key={role.id} className="inline-flex items-center gap-1">
            <Badge variant="outline">{role.name}</Badge>
            {canManage ? (
              <button
                type="button"
                aria-label={t('roles_cell.remove', { name: role.name })}
                className="leading-none text-muted-foreground hover:text-destructive disabled:opacity-50"
                disabled={busy}
                onClick={() => onRevoke(role.id)}
              >
                ×
              </button>
            ) : null}
          </span>
        ))
      )}
      {canManage && assignable.length > 0 ? (
        <Select
          aria-label={t('roles_cell.add')}
          value=""
          disabled={busy}
          onChange={(event) => {
            if (event.target.value) onAssign(event.target.value);
          }}
        >
          <option value="">{t('roles_cell.add')}</option>
          {assignable.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
      ) : null}
    </div>
  );
}
